import React, { useLayoutEffect } from "react";
import { withWebModalStack } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModalStack";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";

import { useNavigationHeaderOptions } from "../../../../../common/navigationHeaderOptions";
import { useIntl } from "react-intl";
import { ReceiverStoresSelector } from "../../../WorkspaceEntity/ReceiverStoresSelector";
import { WorkspaceSignalReceiversQuery } from "./__generated__/WorkspaceSignalReceiversQuery.graphql";
import { WorkspaceSignalReceiversSetStoreIdsMutation } from "./__generated__/WorkspaceSignalReceiversSetStoreIdsMutation.graphql";
import { TopicSelector } from "../../../WorkspaceEntity/TopicSelector";
import { WorkspaceSignalReceiversSetTopicIdMutation } from "./__generated__/WorkspaceSignalReceiversSetTopicIdMutation.graphql";

function InnerWorkspaceSignalReceivers(
  props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalReceivers"> & {
    fetchKey: string;
  }
) {
  const data = useLazyLoadQuery<WorkspaceSignalReceiversQuery>(
    graphql`
      query WorkspaceSignalReceiversQuery(
        $workspaceSignalId: ID!
        $workspaceId: ID!
      ) {
        workspace(id: $workspaceId) @required(action: THROW) {
          topics {
            ...TopicSelector_orgTopic
          }
          signal(id: $workspaceSignalId) @required(action: THROW) {
            id
            status
            updatedAtDate {
              isoString
              ...FormattedDate
            }
            topic {
              id
              isPrivate
            }
            receiverStores {
              id
            }
            potentialReceiverStores {
              ...ReceiverStoresSelector_receiverStores
              ...ReceiverStoresSelectorListItem_receiverStore
            }

            updatedBy {
              name
              id
            }
          }
        }
      }
    `,
    {
      workspaceSignalId: props.route.params.signalId,
      workspaceId: props.route.params.workspaceId,
    },
    {
      fetchPolicy: "store-or-network",
      fetchKey: props.fetchKey,
    }
  );

  const [setStoreIds] =
    useMutation<WorkspaceSignalReceiversSetStoreIdsMutation>(graphql`
      mutation WorkspaceSignalReceiversSetStoreIdsMutation(
        $id: ID!
        $storeIds: [ID!]
      ) @raw_response_type {
        signalWorkspace(id: $id) {
          updateReceiverStoreIds(storeIds: $storeIds) {
            id
            signalCount
            receiverStores {
              id
            }
            updatedAtDate {
              isoString
              ...FormattedDate
            }
            ...WorkspaceSignalEditMainEditedInfo_Signal
          }
        }
      }
    `);

  const receiverStoreIds = data.workspace.signal.receiverStores.map(
    (store) => store.id
  );

  const [setTopicId] = useMutation<WorkspaceSignalReceiversSetTopicIdMutation>(
    graphql`
      mutation WorkspaceSignalReceiversSetTopicIdMutation(
        $id: ID!
        $topicId: ID
      ) @raw_response_type {
        signalWorkspace(id: $id) {
          updateReceiverTopicId(topicId: $topicId) {
            id
            potentialReceiverStores {
              ...ReceiverStoresSelectorListItem_receiverStore
            }
            topic {
              id
            }
            receiverStores {
              id
            }
          }
        }
      }
    `
  );

  const onTopicSelect = (topicId: string | undefined) => {
    setTopicId({
      variables: { id: data.workspace.signal.id, topicId },
      optimisticResponse: {
        signalWorkspace: {
          updateReceiverTopicId: {
            id: data.workspace.signal.id,
            topic: topicId ? { id: topicId } : null,
            receiverStores: data.workspace.signal.receiverStores,
            potentialReceiverStores: undefined as any,
          },
        },
      },
    });
  };

  const disabled = data.workspace.signal.status !== "DRAFT";
  const hasTopics = (data.workspace.topics?.length ?? 0) > 0;
  return (
    <>
      {hasTopics ? (
        <TopicSelector
          topics={data.workspace.topics ?? []}
          currentTopicId={data.workspace.signal.topic?.id}
          onTopicSelect={onTopicSelect}
        />
      ) : null}
      <ReceiverStoresSelector
        disabled={disabled}
        selectedStoreIds={receiverStoreIds}
        stores={data.workspace.signal.potentialReceiverStores}
        isTopicPrivate={data.workspace.signal.topic?.isPrivate ?? false}
        onStoreSetSelect={(newSelectedStoreIds) => {
          const optimisticNewIds = newSelectedStoreIds.map((id) => ({ id }));

          setStoreIds({
            variables: {
              id: props.route.params.signalId,
              storeIds: newSelectedStoreIds,
            },
            optimisticResponse: {
              signalWorkspace: {
                updateReceiverStoreIds: {
                  id: props.route.params.signalId,
                  signalCount: optimisticNewIds.length,
                  receiverStores: optimisticNewIds,
                  updatedAtDate: data.workspace.signal.updatedAtDate
                    ? {
                        __isDate: "UnzonedDate" as const,
                        isoString:
                          data.workspace.signal.updatedAtDate.isoString,
                      }
                    : null,
                  updatedBy: data.workspace.signal.updatedBy,
                },
              },
            },
          });
        }}
      />
    </>
  );
}

export const WorkspaceSignalReceivers = withWebModalStack(
  (props: UserLoggedInNavigatorScreenProps<"WorkspaceSignalReceivers">) => {
    const getOpt = useNavigationHeaderOptions();
    const intl = useIntl();
    useLayoutEffect(() => {
      props.navigation.setOptions(
        getOpt({
          type: "CLOSE",
          onClose: props.navigation.goBack,
          title: intl.formatMessage({
            defaultMessage: "Recipients",
            description: "Workspace signal edit recipients label",
          }),
        })
      );
    }, [getOpt, props, intl]);
    return (
      <LazyQueryNetworkBoundrary newFetchKeyOnNavigation>
        {(fetchKey) => (
          <InnerWorkspaceSignalReceivers fetchKey={fetchKey} {...props} />
        )}
      </LazyQueryNetworkBoundrary>
    );
  },
  true,
  {
    contentStyle: {
      width: 600,
      maxHeight: 600,
    },
  }
);
